import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { draftNegotiation, sendFeedback, engagementMessages, staffSendMessage, staffSubmitFinalOffer, reopenFinal, staffBidTemplateUrl, importStaffBidSheet, rmb, finalPriceLabel, apiErrText, DELIVERY_STATUS, type BidFeedback, type Bid, type Engagement, type PackageDetail, type EngagementMessage } from '../lib/api';
import { Modal } from './ui/Modal';

/** One editable line of the staff-authored final offer (prefilled from the labor's latest round). */
type FoRow = { key: string; work_item_id: string | null; item_label: string; qty: number; unit: string | null; orig: number | null; price: string };

export function NegotiationPanel({ pkg, eng, onClose }: { pkg: PackageDetail; eng: Engagement; onClose: () => void }) {
  const [fb, setFb] = useState<BidFeedback | null>(null);
  const [draft, setDraft] = useState('');
  const [sentResult, setSentResult] = useState<BidFeedback | null>(null);
  const thread = useQuery<EngagementMessage[]>({ queryKey: ['eng-messages', eng.id], queryFn: () => engagementMessages(pkg.id, eng.id) });
  const negotiate = useMutation({
    mutationFn: (message?: string) => draftNegotiation(pkg.id, eng.id, message),
    onSuccess: (r) => { setFb(r); setDraft(r.draft_message_md || suggestionsToText(r)); },
  });
  const send = useMutation({
    mutationFn: (message: string) => sendFeedback(fb!.id!, message),
    onSuccess: (r) => { setSentResult(r); void thread.refetch(); },
  });
  const [msg, setMsg] = useState('');
  const compose = useMutation({
    mutationFn: (body: string) => staffSendMessage(pkg.id, eng.id, body),
    onSuccess: () => { setMsg(''); void thread.refetch(); },
  });
  const qc = useQueryClient();
  // Phase B · 拟定最终报价: staff edit the labor's latest round line-by-line into the final offer.
  const [foRows, setFoRows] = useState<FoRow[] | null>(null); // null = editor closed
  const wiOf = (id: string | null) => pkg.work_items.find((w) => w.id === id);
  function openFoEditor() {
    // fb_e5f31dc1 · prefill the 税金点 when the BOQ carries ONE uniform 税率 (else the estimator types it)
    const rates = [...new Set(pkg.work_items.map((w) => w.tax_rate_pct).filter((t): t is number => t != null))];
    setFoTax(rates.length === 1 ? String(rates[0]) : '');
    // prefill from the labor's OWN latest round — never a (possibly withdrawn) staff final-offer round.
    const latest: Bid | undefined = [...eng.bids].filter((b) => !b.archived && !b.is_final_offer).sort((a, b) => b.round_number - a.round_number)[0];
    setFoRows((latest?.line_items ?? []).map((l) => {
      const w = wiOf(l.work_item_id);
      return {
        key: l.id, work_item_id: l.work_item_id,
        item_label: w?.item_label ?? l.item_label ?? '—',
        qty: l.work_item_id ? (w?.qty ?? 1) : (l.qty_quoted ?? 1),
        unit: w?.unit ?? null, orig: l.unit_price_rmb,
        price: l.unit_price_rmb != null ? String(l.unit_price_rmb) : '',
      };
    }));
  }
  const foTotal = (foRows ?? []).reduce((s, r) => { const p = Number(r.price); return Number.isNaN(p) || r.price === '' ? s : s + p * r.qty; }, 0);
  // fb_e5f31dc1 (改改): the single 税金点 sent with the final offer — the lock shows 不含税 · 税率 · 含税.
  const [foTax, setFoTax] = useState('');
  const foTaxNum = foTax.trim() === '' ? null : Number(foTax);
  const foTaxValid = foTaxNum == null || (!Number.isNaN(foTaxNum) && foTaxNum >= 0 && foTaxNum <= 100);
  // fb_de06464b (改改): author the negotiated 清单 in Excel and import it wholesale — reuses the staff
  // bid template (carries current prices) + the parse-only /bid-import; nothing commits until 发送.
  const [foImportMsg, setFoImportMsg] = useState<string | null>(null);
  async function onFoImport(ev: React.ChangeEvent<HTMLInputElement>) {
    const f = ev.target.files?.[0]; ev.target.value = '';
    if (!f || !foRows) return;
    try {
      const r = await importStaffBidSheet(pkg.id, eng.id, f);
      const byId = new Map(r.line_items.filter((l) => l.work_item_id).map((l) => [l.work_item_id as string, l.unit_price_rmb]));
      const byLabel = new Map(r.line_items.filter((l) => !l.work_item_id && l.item_label).map((l) => [l.item_label as string, l.unit_price_rmb]));
      let hit = 0;
      const next = foRows.map((row) => {
        const p = row.work_item_id ? byId.get(row.work_item_id) : byLabel.get(row.item_label);
        if (p != null) { hit += 1; return { ...row, price: String(p) }; }
        return row;
      });
      setFoRows(next);
      if (r.tax_pct != null) setFoTax(String(r.tax_pct));   // the sheet's single 税率 cell rides along
      setFoImportMsg(hit === 0
        ? '⚠ 未匹配到任何行 — 请使用「⬇ 下载报价模板」的文件填写后再导入 / 0 rows matched — fill in the downloaded template'
        : `已导入 ${hit} 项${r.unmatched ? `（忽略 ${r.unmatched} 行非本包项）` : ''} / ${hit} imported`);
    } catch (e: any) { setFoImportMsg(`⚠ ${e?.body?.error ?? e?.message ?? e}`); }
  }
  const sendFo = useMutation({
    mutationFn: () => staffSubmitFinalOffer(pkg.id, eng.id, (foRows ?? [])
      .filter((r) => r.price !== '' && !Number.isNaN(Number(r.price)))
      .map((r) => ({ work_item_id: r.work_item_id, unit_price_rmb: Number(r.price), ...(r.work_item_id ? {} : { item_label: r.item_label, qty: r.qty }) })), foTaxValid ? foTaxNum : null),
    onSuccess: () => { setFoRows(null); void qc.invalidateQueries({ queryKey: ['package', pkg.id] }); void thread.refetch(); },
  });
  const reopen = useMutation({
    mutationFn: () => reopenFinal(pkg.id, eng.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['package', pkg.id] }); void thread.refetch(); },
  });

  const neg = fb?.negotiation_json ? JSON.parse(fb.negotiation_json) : null;
  const suggestions: any[] = fb?.ai_suggestions_json ? JSON.parse(fb.ai_suggestions_json) : [];
  const walkedAway = neg?.walk_away_triggered === true;

  return (
    <Modal onClose={onClose} title={<>💬 议价助手 / Negotiate — {eng.labor_co_name ?? '劳务方'}</>} width={720}>
        {!fb && (
          <button className="btn-primary" disabled={negotiate.isPending} onClick={() => negotiate.mutate(undefined)}>
            {negotiate.isPending ? '草拟中… / Drafting…' : '🤖 生成议价建议 / Draft advice'}
          </button>
        )}
        {/* fb_6502be27 (改改): the server's real reason (e.g. 「AI 暂不可用」 when no API key is configured)
            lives in body.error — err.message is just "503 Service Unavailable", which read as a bare failure. */}
        {negotiate.isError && <div className="small" style={{ color: 'var(--bad)' }}>{String((negotiate.error as any)?.body?.error ?? (negotiate.error as any)?.message)}</div>}

        {/* Phase B · 议价 → 最终价锁定: staff author the final round line-by-line → labor confirms → locked. */}
        <div style={{ margin: '10px 0', borderTop: '1px solid var(--rule)', paddingTop: 10 }}>
          {eng.final_confirmed_at ? (
            <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
              <span className="pill pill-good">🔒 最终价已确认锁定 {finalPriceLabel(eng.final_total_rmb, eng.final_tax_pct)} / Confirmed & locked</span>
              <button disabled={reopen.isPending} onClick={() => { if (confirm('撤销已锁定的最终价，继续议价？ / Reopen the locked final price?')) reopen.mutate(); }}>撤销锁定 / Reopen</button>
            </div>
          ) : eng.final_confirm_requested_at ? (
            <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
              <span className="pill pill-warn">⏳ 最终报价待劳务确认 {finalPriceLabel(eng.final_total_rmb, eng.final_tax_pct)} / Awaiting labor confirmation</span>
              <button disabled={reopen.isPending} onClick={() => reopen.mutate()}>撤回重拟 / Withdraw & redraft</button>
            </div>
          ) : foRows == null ? (
            <button className="btn-primary" onClick={openFoEditor} disabled={eng.bids.filter((b) => !b.archived).length === 0}
              title="按议价结果逐项调整单价，生成最终报价请劳务确认">
              📝 拟定最终报价 / Draft final offer
            </button>
          ) : (
            <div>
              <div className="small muted" style={{ marginBottom: 4 }}>逐项调整为议定单价（留空 = 该项不报）/ set each negotiated unit price:</div>
              <div className="row" style={{ gap: 10, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <a className="small" href={staffBidTemplateUrl(pkg.id, eng.id)} title="下载含当前报价的模板，线下编辑好后再导入 / download, edit offline, re-import">⬇ 下载报价模板 / Template</a>
                <label className="small" style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                  📥 从xls导入整单 / Import xls
                  <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onFoImport} />
                </label>
                {foImportMsg && <span className="small" style={{ color: foImportMsg.startsWith('⚠') ? 'var(--bad)' : 'var(--good)' }}>{foImportMsg}</span>}
              </div>
              <table className="cmp" style={{ fontSize: 12 }}>
                <thead><tr><th>项目 / Item</th><th>工程量</th><th>原单价 / Was</th><th>最终单价 / Final</th><th>小计 / Amount</th></tr></thead>
                <tbody>
                  {foRows.map((r, i) => {
                    const p = Number(r.price);
                    const amt = r.price !== '' && !Number.isNaN(p) ? p * r.qty : null;
                    return (
                      <tr key={r.key}>
                        <td>{r.item_label}{!r.work_item_id && <span className="pill small pill-muted" style={{ marginLeft: 4 }}>补充</span>}</td>
                        <td className="num">{r.qty}{r.unit ?? ''}</td>
                        <td className="num muted">{rmb(r.orig)}</td>
                        <td><input value={r.price} onChange={(ev) => setFoRows((rows) => rows!.map((x, j) => j === i ? { ...x, price: ev.target.value } : x))} style={{ width: 90, textAlign: 'right' }} /></td>
                        <td className="num">{rmb(amt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr><td colSpan={4} style={{ textAlign: 'right' }}><strong>最终合计（不含税）/ Final total (ex-tax)</strong></td><td className="num money-lg" style={{ fontSize: 15 }}><strong>{rmb(foTotal || null)}</strong></td></tr></tfoot>
              </table>
              {/* fb_e5f31dc1 · 税金点 → 锁定后列明 不含税金额 · 税率 · 含税金额 */}
              <div className="row" style={{ gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="small">税金点 / Tax %</span>
                <input value={foTax} onChange={(e) => setFoTax(e.target.value)} placeholder="如 9" style={{ width: 64, textAlign: 'right', ...(foTaxValid ? {} : { borderColor: 'var(--bad)' }) }} />
                {!foTaxValid && <span className="small" style={{ color: 'var(--bad)' }}>0–100</span>}
                <span className="small muted">
                  {foTaxValid && foTaxNum != null && foTotal > 0
                    ? `→ 含税合计 ${rmb(Math.round(foTotal * (100 + foTaxNum)) / 100)} / incl-tax total`
                    : '选填 · 填写后锁定价将列明 不含税/税率/含税 三项'}
                </span>
              </div>
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn-good" disabled={sendFo.isPending || foTotal <= 0 || !foTaxValid} onClick={() => sendFo.mutate()}>
                  {sendFo.isPending ? '发送中… / Sending…' : `📌 发送并请劳务确认 ${rmb(foTotal || null)} / Send for confirmation`}
                </button>
                <button onClick={() => setFoRows(null)}>取消 / Cancel</button>
              </div>
            </div>
          )}
          {sendFo.isError && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{String((sendFo.error as any)?.body?.error ?? (sendFo.error as any)?.message)}</div>}
          {/* 改改 9/21 · once the subcontract is signed the server refuses 撤销锁定 (409 subcontract_signed) — its
              bilingual detail says where the contract value is amended instead; the bare code said nothing. */}
          {reopen.isError && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{apiErrText(reopen.error)}</div>}
        </div>

        {walkedAway && (
          <div className="pill pill-bad" style={{ margin: '10px 0', display: 'inline-block' }}>
            建议放弃本轮 — 报价高于走开线，可回退备选 / Walk away — revert to runner-up
          </div>
        )}
        {neg && !walkedAway && neg.unresolved_clarifications?.length > 0 && (
          <div className="small" style={{ margin: '8px 0' }}>
            待澄清 / unresolved:{' '}
            {neg.unresolved_clarifications.map((c: string) => (
              <span key={c} className="pill pill-warn" style={{ marginRight: 4 }}>{c}</span>
            ))}
          </div>
        )}

        {suggestions.length > 0 && (
          <table className="cmp" style={{ margin: '10px 0' }}>
            <thead><tr><th>项目 / Item</th><th>报价 / Quote</th><th>市场中位 / Market</th><th>建议目标 / Target</th><th>可信度</th></tr></thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.line_item_id}>
                  <td>{s.item}</td>
                  <td>{rmb(s.observed_unit_price_rmb)}</td>
                  <td>{s.pricing_memory_hint?.market_median ?? '—'}</td>
                  <td>{s.pricing_memory_hint?.suggested_target_unit_rmb ?? '—'}</td>
                  <td><span className="pill pill-muted">{s.pricing_memory_hint?.confidence_tier}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {fb && !fb.id && (
          <div className="small muted" style={{ marginTop: 10 }}>{fb.note ?? '暂无草稿，请重试。 / No draft — please retry.'}</div>
        )}

        {fb && fb.id && (
          <>
            <div className="small muted" style={{ marginBottom: 4 }}>草拟消息（可编辑后发送）/ Draft message (edit before sending):</div>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={6} style={{ width: '100%' }} />
            {send.isError && <div className="small" style={{ color: 'var(--bad)' }}>{String((send.error as any)?.body?.error ?? (send.error as any)?.message)}</div>}
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn-good" disabled={walkedAway || send.isPending || !draft.trim()} onClick={() => send.mutate(draft)}>
                发送 / Send
              </button>
              <button onClick={onClose}>关闭 / Close</button>
            </div>
            {sentResult?.delivery_status && DELIVERY_STATUS[sentResult.delivery_status] && (
              <div className="small" style={{ marginTop: 6 }}>
                投递 / delivery：<span className={`pill small ${DELIVERY_STATUS[sentResult.delivery_status].pill}`}>{DELIVERY_STATUS[sentResult.delivery_status].label}</span>
                {sentResult.delivery_status !== 'success' && <span className="muted"> · 已存站内记录，对方登录后可见 / logged in-app regardless</span>}
              </div>
            )}
            <div className="small muted" style={{ marginTop: 6 }}>
              你需要把对方修改后的报价通过「✍️ 录入报价」录入为下一轮。 / Enter the revised quote as the next round via "Enter bid".
            </div>
          </>
        )}

        {/* two-way thread — outbound staff messages + inbound labor replies */}
        {(thread.data ?? []).length > 0 && (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--rule)', paddingTop: 10 }}>
            <div className="small muted" style={{ marginBottom: 4 }}>沟通记录 / Message thread</div>
            {(thread.data ?? []).map((m) => (
              <div key={m.id} className="row-wrap" style={{ gap: 6, alignItems: 'baseline', padding: '3px 0' }}>
                <span className={`pill small ${m.direction === 'outbound' ? 'pill-muted' : 'pill-stage'}`}>{m.direction === 'outbound' ? 'WTL' : '对方 / them'}</span>
                <span style={{ flex: 1 }}>{m.body_md}</span>
                {m.direction === 'outbound' && DELIVERY_STATUS[m.delivery_status] && <span className={`pill small ${DELIVERY_STATUS[m.delivery_status].pill}`}>{DELIVERY_STATUS[m.delivery_status].label}</span>}
              </div>
            ))}
          </div>
        )}

        {/* WS5a · free-text composer — message the labor co directly (not just bid-feedback) */}
        <div className="row-wrap" style={{ gap: 6, marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 10 }}>
          <input className="grow" style={{ minWidth: 240 }} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={`发消息给 ${eng.labor_co_name ?? '劳务方'} / message the labor co`} />
          <button disabled={!msg.trim() || compose.isPending} onClick={() => compose.mutate(msg)}>{compose.isPending ? '发送中… / Sending…' : '发送 / Send'}</button>
        </div>
    </Modal>
  );
}

function suggestionsToText(fb: BidFeedback): string {
  const s: any[] = fb.ai_suggestions_json ? JSON.parse(fb.ai_suggestions_json) : [];
  return s.map((x) => `${x.item}：${x.pricing_memory_hint?.rationale_zh ?? ''}`).join('\n');
}

